import { useContext } from 'react';
import { useRouter } from 'next/router';
import { styled } from '@nx-kit/styling';
import { BreakpointContext } from 'contexts/breakpoint/BreakpointContext';
import getDevice from 'helpers/getDevice';
import getSpacing from './helpers/getSpacing';

interface CmsElmtWrapperProps {
  id?: string;
  nextSlice?: string;
  sliceName: string;
  children?: React.ReactNode;
}

const Div = styled.div<{ spacing: number }>`
  margin-bottom: ${(props) => props.spacing}px;
`;

const CmsElmtWrapper: React.FC<CmsElmtWrapperProps> = ({ nextSlice, sliceName, id, children }) => {
  const { asPath, route, query } = useRouter();
  const breakpoint = useContext(BreakpointContext);
  const spacingBreakpoint = getDevice(breakpoint, 'smartphone') ? 'xs' : 'xl';

  // Museum-partners page only: tighten the gap between consecutive image_text
  // (logo) blocks. Everywhere else image_text keeps its normal spacing.
  const isPartnerLogoGap =
    asPath.includes('museumspartner') && sliceName === 'image_text' && nextSlice === 'image_text';

  // Herbstmarkt event page only: the intro text and the programme tables fall
  // back to the generic 140px desktop gap, which reads as a page break between
  // the programme days. Every other page keeps its normal slice spacing.
  const isHerbstmarktContentGap =
    route === '/[domain]/events/[slug]' &&
    query.slug === 'herbstmarkt' &&
    nextSlice !== undefined &&
    (sliceName === 'rich_text' || sliceName === 'table_2_cols');

  let spacing = getSpacing(spacingBreakpoint, sliceName, nextSlice);

  if (isPartnerLogoGap) {
    spacing = spacingBreakpoint === 'xs' ? 16 : 50;
  } else if (isHerbstmarktContentGap && spacingBreakpoint === 'xl') {
    spacing = 60;
  }

  return (
    <Div as="section" id={id} spacing={spacing} data-slice-name={sliceName}>
      {children}
    </Div>
  );
};

export default CmsElmtWrapper;
